import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const useUserStore = defineStore('users', () => {
  const uiStore = useUIStore()

  // State
  const users = ref([])
  const clients = ref([])
  const admins = ref([])
  const developers = ref([])
  const userStats = ref({})
  const loading = ref(false)

  // Actions
  const initializeUsers = async () => {
    const authStore = (await import('./auth')).useAuthStore()

    if (authStore.user?.role !== 'admin') {
      logService.info('Skipping user initialization: User is not an admin')
      return { data: [], success: true }
    }

    try {
      loading.value = true
      logService.info('Fetching users...')

      const response = await apiService.get('/admin/users')

      if (response.success) {
        users.value = response.data
        updateUserCategories()
        updateUserStats()
        return response
      } else {
        throw new Error(response.message || 'Failed to fetch users')
      }
    } catch (error) {
      logService.error('Fetch Users Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { data: [], success: false }
    } finally {
      loading.value = false
    }
  }

  const addUser = async (userData) => {
    try {
      loading.value = true
      logService.info('Adding user...', userData)

      const response = await apiService.post('/admin/users', userData)

      if (response.success) {
        const newUser = response.data
        users.value.push(newUser)
        updateUserCategories()
        updateUserStats()

        uiStore.showNotification({
          type: 'success',
          title: 'User Added',
          message: `${userData.name} has been added successfully.`
        })

        return { success: true, user: newUser }
      } else {
        throw new Error(response.message || 'Registration failed')
      }
    } catch (error) {
      logService.error('Add User Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const updateUser = async (userId, userData) => {
    try {
      loading.value = true
      logService.info(`Updating user: ${userId}`, userData)

      const response = await apiService.put(`/admin/users/${userId}`, userData)

      if (response.success) {
        const index = users.value.findIndex(u => u.id === userId)
        if (index !== -1) {
          users.value[index] = {
            ...users.value[index],
            ...response.data
          }
        }

        updateUserCategories()
        updateUserStats()

        uiStore.showNotification({
          type: 'success',
          title: 'User Updated',
          message: `${userData.name || 'User'} has been updated successfully.`
        })

        return { success: true, user: users.value[index] }
      } else {
        throw new Error(response.message || 'Update failed')
      }
    } catch (error) {
      logService.error(`Update User Error [${userId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const deleteUser = async (userId) => {
    try {
      loading.value = true
      logService.info(`Deleting user: ${userId}`)

      const response = await apiService.delete(`/admin/users/${userId}`)

      if (response.success) {
        const index = users.value.findIndex(u => u.id === userId)
        if (index !== -1) {
          const userName = users.value[index].name
          users.value.splice(index, 1)

          updateUserCategories()
          updateUserStats()

          uiStore.showNotification({
            type: 'success',
            title: 'User Deleted',
            message: `${userName} has been deleted successfully.`
          })

          return { success: true }
        }
      } else {
        throw new Error(response.message || 'Deletion failed')
      }
    } catch (error) {
      logService.error(`Delete User Error [${userId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const assignRole = async (userId, newRole) => {
    try {
      loading.value = true
      logService.info(`Assigning role ${newRole} to user: ${userId}`)

      const response = await apiService.put(`/admin/users/${userId}/role`, { role: newRole })

      if (response.success) {
        const user = users.value.find(u => u.id === userId)
        if (user) {
          user.role = newRole
          updateUserCategories()
          updateUserStats()
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Role Updated',
          message: `Role changed to ${newRole} successfully.`
        })

        return { success: true }
      } else {
        throw new Error(response.message || 'Role assignment failed')
      }
    } catch (error) {
      logService.error(`Assign Role Error [${userId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const toggleUserStatus = async (userId) => {
    try {
      loading.value = true
      logService.info(`Toggling status for user: ${userId}`)

      const response = await apiService.post(`/admin/users/${userId}/toggle-status`)

      if (response.success) {
        const user = users.value.find(u => u.id === userId)
        if (user) {
          user.status = response.data.status
          updateUserStats()
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Status Updated',
          message: `User status updated successfully.`
        })

        return { success: true }
      } else {
        throw new Error(response.message || 'Status toggle failed')
      }
    } catch (error) {
      logService.error(`Toggle Status Error [${userId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const updateLastLogin = async (userId) => {
    try {
      const user = users.value.find(u => u.id === userId)
      if (user) {
        user.last_login = new Date().toISOString()
      }
    } catch (error) {
      logService.error('Update Last Login Error:', error)
    }
  }

  const updateUserCategories = () => {
    clients.value = users.value.filter(u => u.role === 'client')
    admins.value = users.value.filter(u => u.role === 'admin')
    developers.value = users.value.filter(u => u.role === 'developer')
  }

  const updateUserStats = () => {
    const stats = {
      total: users.value.length,
      active: users.value.filter(u => u.status === 'active').length,
      inactive: users.value.filter(u => u.status === 'inactive').length,
      clients: users.value.filter(u => u.role === 'client').length,
      admins: users.value.filter(u => u.role === 'admin').length,
      developers: users.value.filter(u => u.role === 'developer').length,
      recent_signups: users.value.filter(u => {
        const signupDate = new Date(u.created_at)
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
        return signupDate > thirtyDaysAgo
      }).length
    }

    userStats.value = stats
  }

  const getUserById = (id) => {
    return users.value.find(user => user.id === id)
  }

  const getUsersByRole = (role) => {
    return users.value.filter(user => user.role === role)
  }

  const getUsersByStatus = (status) => {
    return users.value.filter(user => user.status === status)
  }

  const searchUsers = (query) => {
    const lowercaseQuery = query.toLowerCase()
    return users.value.filter(user =>
      user.name.toLowerCase().includes(lowercaseQuery) ||
      user.email.toLowerCase().includes(lowercaseQuery) ||
      (user.company && user.company.toLowerCase().includes(lowercaseQuery))
    )
  }

  return {
    // State
    users,
    clients,
    admins,
    developers,
    userStats,
    loading,

    // Actions
    initializeUsers,
    addUser,
    updateUser,
    deleteUser,
    assignRole,
    toggleUserStatus,
    updateLastLogin,
    updateUserCategories,
    updateUserStats,

    // Getters
    getUserById,
    getUsersByRole,
    getUsersByStatus,
    searchUsers
  }
})
